/**
 * Editing a note's text, pin and who can see it: the form, and the save with its optimistic cache
 * updates and messages. Shared by `EditMemoForm` and the desktop project record's note pane.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { type EventFeedResponse, Memo, type MemoList } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import { queryKeys } from '../../../../../hooks/react-query/constants';
import { useUpdateMemoMutation } from '../../../../../hooks/react-query/useMemoQuery';
import { type ZodObjectWithModelKeys } from '../../../../../tools/typeguargs';

type EditMemoFormFields = {
  description: string;
  isPinned: boolean;
  isInternal: boolean;
};

const editMemoFormSchema = z.object({
  description: z.string().max(5000, 'Description is too long (max 5000 characters)'),
  isPinned: z.boolean(),
  isInternal: z.boolean(),
}) satisfies ZodObjectWithModelKeys<EditMemoFormFields>;

export type EditMemoFormSchemaType = z.infer<typeof editMemoFormSchema>;

export const useEditMemoForm = ({
  memo,
  onClose,
  onSuccess,
  onBusyChange,
  notify = true,
}: {
  memo: Memo;
  onClose?: () => void;
  onSuccess?: () => void;
  /** Tells a surrounding dialog when the save is running, so it can't be closed mid-save. */
  onBusyChange?: (isBusy: boolean) => void;
  /** False leaves the success and failure messages to the caller, which reads the result. */
  notify?: boolean;
}) => {
  const { mutateAsync: updateMemo, isPending } = useUpdateMemoMutation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!onBusyChange) return;
    onBusyChange(isPending);
    return () => onBusyChange(false);
  }, [isPending, onBusyChange]);

  const form = useForm<EditMemoFormSchemaType>({
    resolver: zodResolver(editMemoFormSchema),
    mode: 'onBlur',
    defaultValues: {
      description: memo.description || '',
      isPinned: memo.isPinned || false,
      isInternal: memo.isInternal ?? true,
    },
  });

  // Reset form when memo prop changes to ensure we have the latest data
  useEffect(() => {
    form.reset({
      description: memo.description || '',
      isPinned: memo.isPinned || false,
      isInternal: memo.isInternal ?? true,
    });
  }, [memo, form]);

  /** Saves the note; resolves true when it saved. */
  const saveNote = async (data: EditMemoFormSchemaType): Promise<boolean> => {
    const updatedMemo = Memo.create({
      ...memo,
      description: data.description,
      isPinned: data.isPinned,
      isInternal: data.isInternal,
    });

    // Check if isPinned changed - if so, we need to refetch to get proper sorting
    const isPinnedChanged = (memo.isPinned || false) !== data.isPinned;

    // Only do optimistic updates if isPinned didn't change
    if (!isPinnedChanged) {
      // Optimistically update the query cache
      queryClient.setQueryData<MemoList>(
        [queryKeys.memo.root, queryKeys.memo.getMemoHash({ id: memo.id })],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            results: [updatedMemo],
          };
        },
      );

      // Also update the memo list cache
      queryClient.setQueriesData<MemoList>({ queryKey: [queryKeys.memoList.root] }, (oldData) => {
        if (!oldData?.results) return oldData;
        return {
          ...oldData,
          results: oldData.results.map((m) => (m.id === memo.id ? updatedMemo : m)),
        };
      });

      // Update event feed cache
      queryClient.setQueriesData<EventFeedResponse>(
        { queryKey: [queryKeys.eventFeed.root] },
        (oldData) => {
          if (!oldData?.results) return oldData;
          return {
            ...oldData,
            results: oldData.results.map((item) =>
              item.feedType === 'memo' && item.sourceId === memo.id
                ? {
                    ...item,
                    content: data.description,
                    isPinned: data.isPinned,
                    isInternal: data.isInternal,
                  }
                : item,
            ),
          };
        },
      );
    }

    try {
      await updateMemo({
        ...updatedMemo,
        fieldMask: ['Description', 'IsPinned', 'IsInternal'],
      });

      if (notify) {
        toast({
          title: 'Note updated successfully',
          description: 'Your changes have been saved',
        });
      }

      onSuccess?.();
      onClose?.();

      // If isPinned changed, invalidate queries to refetch with proper sorting
      // Do this after closing the dialog so it doesn't block the UI
      if (isPinnedChanged) {
        queryClient.invalidateQueries({ queryKey: [queryKeys.memo.root] });
        queryClient.invalidateQueries({ queryKey: [queryKeys.memoList.root] });
        queryClient.invalidateQueries({ queryKey: [queryKeys.eventFeed.root] });
      }
      return true;
    } catch (error) {
      console.error('Failed to update memo:', error);

      // Rollback optimistic updates on error
      queryClient.invalidateQueries({ queryKey: [queryKeys.memo.root] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.memoList.root] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.eventFeed.root] });

      if (notify) {
        toast({
          title: 'Failed to update note',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
      return false;
    }
  };
  const handleSubmit: SubmitHandler<EditMemoFormSchemaType> = async (data) => {
    await saveNote(data);
  };

  return { form, handleSubmit, saveNote, isPending };
};
